import React from 'react';
import { Button, Text } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import classNames from 'classnames';

import './styles.scss';
import ItemSkeleton from './ItemSkeleton';
import UpcomingEventItem from './Item';

type EmptyStateProps = {
  message: string;
  buttonLabel?: string;
  buttonTitle?: string;
  onButtonClick?: () => void;
  dataAnalyticsId?: string;
};

type Props = {
  className?: string;
  days?: number;
  amount?: number;
  children: React.ReactNode;
  isLoading?: boolean;
  emptyState?: EmptyStateProps;
};

/**
 * Reusable empty state component for UpcomingEvents
 */
const EmptyState = ({
  message,
  buttonLabel,
  buttonTitle,
  onButtonClick,
  dataAnalyticsId,
}: EmptyStateProps) => {
  const hasButton = buttonLabel && onButtonClick;

  return (
    <>
      <div
        className={classNames('upcoming-events__empty', {
          'upcoming-events__empty--no-button': !hasButton,
        })}
      >
        <Text>{message}</Text>
      </div>
      {hasButton && (
        <div className="upcoming-events__empty-footer">
          <Button
            data-testid="empty-state-redirect"
            className="upcoming-events__empty-btn"
            variant="secondary"
            title={buttonTitle}
            onClick={onButtonClick}
            data-analytics-id={dataAnalyticsId}
          >
            {buttonLabel}
          </Button>
        </div>
      )}
    </>
  );
};

/**
 * Generic component to display the upcoming events that the user is enrolled to
 */
const UpcomingEvents = ({
  children,
  amount,
  days = 7,
  className,
  isLoading,
  emptyState,
}: Props) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div
        data-testid="loading"
        className={classNames('upcoming-events', className)}
      >
        <div className="upcoming-events__list">
          <ItemSkeleton />
          <ItemSkeleton />
          <ItemSkeleton />
        </div>
      </div>
    );
  }

  if (emptyState) {
    return (
      <div
        data-testid="empty-state"
        className={classNames('upcoming-events', className)}
      >
        <EmptyState {...emptyState} />
      </div>
    );
  }

  return (
    <div className={classNames('upcoming-events', className)}>
      {amount !== undefined && (
        <div className="upcoming-events__header">
          {t(
            'You have {{amount}} upcoming live classes in the next {{days}} days.',
            {
              amount,
              days,
            },
          )}
        </div>
      )}
      <div className="upcoming-events__list">{children}</div>
    </div>
  );
};

export { EmptyState };
export { UpcomingEventItem as Item };
export default withErrorBoundary(UpcomingEvents, ComponentError, handleError);
